
import { motion } from "framer-motion"
import { Check, X } from "lucide-react"

interface PasswordStrengthIndicatorProps {
  password: string
}

export const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps) => {
  const requirements = [
    { label: "At least 6 characters", test: (pwd: string) => pwd.length >= 6 }
  ]

  const passedRequirements = requirements.filter((req) => req.test(password)).length
  const strength = (passedRequirements / requirements.length) * 100

  const getStrengthColor = () => {
    if (strength < 40) return "bg-red-500"
    if (strength < 70) return "bg-amber-500"
    return "bg-green-500"
  }

  const getStrengthText = () => {
    if (strength < 40) return "Weak"
    if (strength < 70) return "Medium"
    return "Strong"
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Password Strength</span>
        <span
          className={`text-sm font-semibold ${strength < 40 ? "text-red-600" : strength < 70 ? "text-amber-600" : "text-green-600"}`}
        >
          {getStrengthText()}
        </span>
      </div>

      {/* Strength Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${strength}%` }}
          transition={{ duration: 0.3 }}
          className={`h-2 rounded-full ${getStrengthColor()}`}
        />
      </div>

      {/* Requirements List */}
      <div className="space-y-2">
        {requirements.map((requirement, index) => {
          const isPassed = requirement.test(password)
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center gap-2"
            >
              <div
                className={`w-4 h-4 rounded-full flex items-center justify-center ${isPassed ? "bg-green-100" : "bg-gray-200"}`}
              >
                {isPassed ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-gray-400" />}
              </div>
              <span className={`text-sm ${isPassed ? "text-green-700" : "text-gray-600"}`}>{requirement.label}</span>
            </motion.div>
          )
        })}
      </div>
    </motion.div>
  )
}
